import { useCallback } from 'react';
import { useSceneStore } from '../stores/sceneStore';
import { getSpineManager } from '../lib/spine-manager';
import { api } from '../lib/api-client';
import type { Asset } from '@spine-workspace/shared';
import { MAX_SCENE_ASSETS } from '@spine-workspace/shared';

/** Returns true for image assets regardless of camelCase vs snake_case API response */
function isImageAsset(asset: Asset): boolean {
  return asset.spineVersion === 'image' ||
    (asset as unknown as Record<string, unknown>)['spine_version'] === 'image';
}

async function resolveImageUrl(asset: Asset): Promise<string> {
  const res = await api.get<{ asset: Asset }>(`/api/assets/${asset.id}`);
  return res.asset.jsonUrl!;
}

function getWorldCenter(canvasWidth: number, canvasHeight: number) {
  const manager = getSpineManager();
  const viewport = manager.viewportInstance;
  let worldX = canvasWidth / 2;
  let worldY = canvasHeight / 2;
  if (viewport) {
    const screenCenterX = (viewport.screenWidth ?? canvasWidth) / 2;
    const screenCenterY = (viewport.screenHeight ?? canvasHeight) / 2;
    const worldPt = viewport.toWorld(screenCenterX, screenCenterY);
    if (Number.isFinite(worldPt.x)) worldX = worldPt.x;
    if (Number.isFinite(worldPt.y)) worldY = worldPt.y;
  }
  return { worldX, worldY };
}

async function addSingleAsset(
  asset: Asset,
  worldX: number,
  worldY: number,
  addLayer: (assetId: string, name: string, animations: string[], x: number, y: number) => void,
  addImageLayer: (assetId: string, name: string, imageUrl: string, x: number, y: number) => void,
) {
  if (isImageAsset(asset)) {
    const imageUrl = await resolveImageUrl(asset);
    addImageLayer(asset.id, asset.name, imageUrl, worldX, worldY);
  } else {
    addLayer(asset.id, asset.name, asset.metadata?.animations ?? [], worldX, worldY);
  }
}

export function useDragDrop() {
  const { addLayer, addImageLayer, layers, canvasWidth, canvasHeight } = useSceneStore();

  // pixelX/pixelY are already relative to the PixiJS canvas element (computed in SceneCanvas)
  const handleDropOnCanvas = useCallback(
    async (assetData: string, pixelX: number, pixelY: number) => {
      let assets: Asset[];
      try {
        const parsed = JSON.parse(assetData);
        assets = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return;
      }

      const manager = getSpineManager();
      const viewport = manager.viewportInstance;

      // Convert drop pixel position to world coords once
      let baseWorldX = 0;
      let baseWorldY = 0;
      if (viewport) {
        const worldPt = viewport.toWorld(pixelX, pixelY);
        if (Number.isFinite(worldPt.x)) baseWorldX = worldPt.x;
        if (Number.isFinite(worldPt.y)) baseWorldY = worldPt.y;
      }

      const STAGGER = 10; // world-space offset per additional asset
      for (let i = 0; i < assets.length; i++) {
        if (layers.length + i >= MAX_SCENE_ASSETS) {
          alert(`Max ${MAX_SCENE_ASSETS} assets per scene.`);
          break;
        }
        const asset = assets[i];
        await addSingleAsset(
          asset,
          baseWorldX + i * STAGGER,
          baseWorldY + i * STAGGER,
          addLayer,
          addImageLayer,
        );
      }
    },
    [layers.length, addLayer, addImageLayer, canvasWidth, canvasHeight]
  );

  /** Add asset(s) at canvas center — called on double-click in asset library */
  const addAssetAtCenter = useCallback(
    async (asset: Asset) => {
      if (layers.length >= MAX_SCENE_ASSETS) {
        alert(`Max ${MAX_SCENE_ASSETS} assets per scene.`);
        return;
      }

      const { worldX, worldY } = getWorldCenter(canvasWidth, canvasHeight);
      await addSingleAsset(asset, worldX, worldY, addLayer, addImageLayer);
    },
    [layers.length, addLayer, addImageLayer, canvasWidth, canvasHeight]
  );

  /** Add multiple assets at canvas center with stagger */
  const addAssetsAtCenter = useCallback(
    async (assets: Asset[]) => {
      const { worldX, worldY } = getWorldCenter(canvasWidth, canvasHeight);
      const STAGGER = 10;
      for (let i = 0; i < assets.length; i++) {
        if (layers.length + i >= MAX_SCENE_ASSETS) {
          alert(`Max ${MAX_SCENE_ASSETS} assets per scene.`);
          break;
        }
        await addSingleAsset(
          assets[i],
          worldX + i * STAGGER,
          worldY + i * STAGGER,
          addLayer,
          addImageLayer,
        );
      }
    },
    [layers.length, addLayer, addImageLayer, canvasWidth, canvasHeight]
  );

  return { handleDropOnCanvas, addAssetAtCenter, addAssetsAtCenter };
}
